import { ActivityWithProgramCount, PrefectureDetail } from '@gyym/sdk'
import dayjs from 'dayjs'
import deepEqual from 'deep-equal'

import { levelOptions } from '@/modules/Programs'
import { genderOptions } from '@/modules/Users'
import { isLocalStorageAvailable } from '@/utils/isLocalStorageAvailable'

import { FACILITY_SEARCH_HISTORY_KEY, initialSearchHistory } from '../constants'
import { SearchHistory } from '../types'

/**
 * Get all search histories
 *
 * @returns SearchHistory[]
 */
const getAllSearchHistories = (): SearchHistory[] => {
  // Return empty array if localStorage is not available
  if (!isLocalStorageAvailable()) return []

  try {
    return JSON.parse(localStorage.getItem(FACILITY_SEARCH_HISTORY_KEY) || '[]')
  } catch {
    return []
  }
}

/**
 * Get all search history including facility name
 */
export const getFacilityNameHistories = (): string[] => {
  const histories = getAllSearchHistories()
  return histories
    .filter((history) => history.facilityName)
    .map((history) => history.facilityName)
    .slice(0, 10) as string[]
}

/**
 * Get first 10 search histories
 *
 * @returns SearchHistory[]
 */
export const get = (): SearchHistory[] => {
  const histories = getAllSearchHistories()
  return histories.slice(0, 10)
}

/**
 * Set search history
 *
 * @param params Partial<SearchHistory>
 * @returns void
 */
export const set = (params: Partial<SearchHistory>): void => {
  const history: SearchHistory = {
    facilityName: params.facilityName ?? initialSearchHistory.facilityName,
    activityIds: params.activityIds ?? initialSearchHistory.activityIds,
    prefectureIds: params.prefectureIds ?? initialSearchHistory.prefectureIds,
    areaIds: params.areaIds ?? initialSearchHistory.areaIds,
    sexLimitations:
      params.sexLimitations ?? initialSearchHistory.sexLimitations,
    exerciseLevels:
      params.exerciseLevels ?? initialSearchHistory.exerciseLevels,
    dates: params.dates ?? initialSearchHistory.dates,
    startTimeFrom: params.startTimeFrom ?? initialSearchHistory.startTimeFrom,
    startTimeTo: params.startTimeTo ?? initialSearchHistory.startTimeTo,
    priceFrom: params.priceFrom ?? initialSearchHistory.priceFrom,
    priceTo: params.priceTo ?? initialSearchHistory.priceTo,
  }

  // Do nothing if history is same as initialSearchHistory
  if (deepEqual(initialSearchHistory, history)) return

  // Return if localStorage is not available
  if (!isLocalStorageAvailable()) return

  const histories = getAllSearchHistories()

  const lastHistory = histories[0]

  // Do nothing if history is same as last history
  if (deepEqual(lastHistory, history)) return

  // Prepend new history
  const newHistories = [history, ...histories].reduce((acc, value) => {
    const countHistoryWithFacilityName = acc.filter(
      (history) => history.facilityName,
    ).length

    // Keep a maximum of 10 search histories including facility name search
    // Note that there is a possibility that more than 10 search histories are stored in local storage
    return countHistoryWithFacilityName >= 10 ? acc : [...acc, value]
  }, [] as SearchHistory[])

  try {
    localStorage.setItem(
      FACILITY_SEARCH_HISTORY_KEY,
      JSON.stringify(newHistories),
    )
  } catch {
    // NOOP
  }
}

/**
 * Convert SearchHistory to readable string
 *
 * @param history SearchHistory to convert
 * @param prefectures PrefectureDetail[]
 * @param activities ActivityWithProgramCount[]
 * @returns string
 */
export const toReadableString = (
  history: SearchHistory,
  prefectures: PrefectureDetail[],
  activities: ActivityWithProgramCount[],
): string => {
  const areas = prefectures.flatMap((pref) => pref.areas)

  return [
    // Facility name search
    history.facilityName ? history.facilityName : null,
    // Prefectures
    history.prefectureIds && history.prefectureIds.length !== 0
      ? history.prefectureIds
          .map((id) => prefectures.find((pref) => pref.id === id)?.name)
          .filter((item) => item)
          .join(',')
      : null,
    // Areas
    history.areaIds.length !== 0
      ? history.areaIds
          .map((id) => areas.find((pref) => pref.id === id)?.name)
          .filter((item) => item)
          .join(',')
      : null,
    // Dates
    history.dates.length !== 0
      ? history.dates.map((date) => dayjs(date).format('M月D日')).join(',')
      : null,
    // Start time
    history.startTimeFrom || history.startTimeTo
      ? `${history.startTimeFrom || ''}~${history.startTimeTo || ''}`
      : null,
    // Activities
    history.activityIds.length !== 0
      ? history.activityIds
          .map((id) => activities.find((activity) => activity.id === id)?.name)
          .filter((item) => item)
          .join(',')
      : null,
    // Prices
    history.priceFrom || history.priceTo
      ? `${
          history.priceFrom === null ? '指定なし' : `${history.priceFrom}pt`
        }〜${history.priceTo === null ? '指定なし' : `${history.priceTo}pt`}`
      : null,
    // Gender
    history.sexLimitations.length !== 0
      ? history.sexLimitations
          .map((g) => genderOptions.find(({ value }) => value === g)?.label)
          .filter((item) => item)
          .join(',')
      : null,
    // Levels
    history.exerciseLevels.length !== 0
      ? history.exerciseLevels
          .map((g) => levelOptions.find(({ value }) => value === g)?.label)
          .filter((item) => item)
          .join(',')
      : null,
  ]
    .filter((item) => item)
    .join(',')
}
